import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate } from "./AccountsShared";
import "../EmployeeAccounts.css";

const money = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const label = (value = "") => String(value || "pending").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export const AccountsQualityPayments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ status: "approved", paymentMode: "bank", referenceNo: "", paymentProof: "", remarks: "" });

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await API.get("/employee/accounts/quality-payment-dispatches");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load quality payment items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettlement = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const endpoint = form.status === "released"
        ? `/employee/accounts/quality-payment-dispatches/${active._id}/settlement/release`
        : `/employee/accounts/quality-payment-dispatches/${active._id}/settlement`;
      await API.patch(endpoint, form);
      setActive(null);
      await load();
    } catch (error) { setErr(error?.response?.data?.message || "Payment update failed"); }
    finally { setSaving(false); }
  };

  const summary = useMemo(() => ({
    total: rows.length,
    amount: rows.reduce((sum, row) => sum + Number(row.qualityInspection?.proposedAmount || 0), 0),
    agreed: rows.filter((row) => row.plantWorkflow?.supplierResponse === "agreed").length,
  }), [rows]);

  return (
    <Container className="accounts-page accounts-module-page">
      <section className="accounts-module-shell">
        <header className="accounts-module-header">
          <div className="accounts-module-title">
            <div className="accounts-module-icon">
              <span className="material-symbols-outlined">payments</span>
            </div>
            <div>
              <h2>Quality Payment Desk</h2>
              <p>Plant approved / supplier agreed quality amounts yahan se payment processing ke liye ready hain.</p>
            </div>
          </div>
          <div className="accounts-module-header-actions">
            <Button variant="outline-secondary" onClick={load}>Refresh</Button>
          </div>
        </header>

        <section className="accounts-module-summary">
          <article><span>Ready Items</span><strong>{summary.total}</strong></article>
          <article><span>Total Amount</span><strong>{money(summary.amount)}</strong></article>
          <article><span>Supplier Agreed</span><strong>{summary.agreed}</strong></article>
        </section>

        {err && <Alert variant="danger" className="mx-4 mb-0">{err}</Alert>}

        <section className="accounts-module-tablecard">
          {loading ? (
            <div className="accounts-module-loading"><Spinner animation="border" /></div>
          ) : (
            <div className="accounts-module-tablewrap">
              <Table responsive className="accounts-module-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Vehicle / PO</th>
                    <th>Material</th>
                    <th>Quality Report</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Forwarded</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-5 text-muted">No quality payment item ready.</td></tr>
                  ) : rows.map((row) => (
                    <tr key={row._id}>
                      <td><strong>{row.companyName || row.supplierName || "-"}</strong><div className="text-muted small">{row.contactMobile || ""}</div></td>
                      <td>{row.vehicle?.number || "-"}<div className="text-muted small">{row.purchaseOrderNo || "-"}</div></td>
                      <td>{row.materialName || row.productName || "-"}<div className="text-muted small">{row.quantity || "-"} {row.unit || ""}</div></td>
                      <td>{row.qualityInspection?.reportNo || "-"}<div className="text-muted small">{label(row.qualityInspection?.status)}</div></td>
                      <td><strong>{money(row.grn?.netPayable ?? row.qualityInspection?.proposedAmount)}</strong><div className="text-muted small">{row.grn?.number || "Legacy"}</div></td>
                      <td>{label(row.plantWorkflow?.status)}</td>
                      <td>{fmtDate(row.plantWorkflow?.accountsForwardedAt)}</td>
                      <td><Button size="sm" onClick={() => { setActive(row); setForm({ status: row.settlement?.status === "approved" ? "released" : "approved", paymentMode: row.settlement?.paymentMode || "bank", referenceNo: "", paymentProof: "", remarks: "" }); }}>Process</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </section>
      </section>
      <Modal show={!!active} onHide={() => setActive(null)} centered>
        <Modal.Header closeButton><Modal.Title>Supplier Settlement</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="info">GRN: <strong>{active?.grn?.number || "Legacy"}</strong><br />Net payable: <strong>{money(active?.grn?.netPayable ?? active?.qualityInspection?.proposedAmount)}</strong></Alert>
          <Form onSubmit={saveSettlement}>
            <Form.Group className="mb-3"><Form.Label>Action</Form.Label><Form.Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="approved">Approve Payment</option><option value="released">Release Payment</option><option value="on-hold">Hold Payment</option><option value="rejected">Reject / Return</option></Form.Select></Form.Group>
            {form.status === "released" && <><Form.Group className="mb-3"><Form.Label>Payment Mode</Form.Label><Form.Select value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}><option value="bank">Bank Transfer</option><option value="upi">UPI</option><option value="cheque">Cheque</option><option value="cash">Cash</option></Form.Select></Form.Group><Form.Group className="mb-3"><Form.Label>Transaction / Reference Number</Form.Label><Form.Control value={form.referenceNo} onChange={(e) => setForm({ ...form, referenceNo: e.target.value })} required /></Form.Group><Form.Group className="mb-3"><Form.Label>Payment Proof URL</Form.Label><Form.Control value={form.paymentProof} onChange={(e) => setForm({ ...form, paymentProof: e.target.value })} /></Form.Group></>}
            <Form.Group className="mb-3"><Form.Label>Remarks</Form.Label><Form.Control as="textarea" rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Form.Group>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save Payment Status"}</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AccountsQualityPayments;
